import React, { useState } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import type {
  QuestDefinition,
  PlayerQuestState,
  QuestUpdateEvent,
  StarterQuestId,
  QuestProgress,
} from '../types/quests';

interface QuestLogPanelProps {
  socket: Socket | null;
  onClose: () => void;
  quests: QuestDefinition[];
  onSetDestination?: (x: number, y: number, name: string) => void;
}

export const QuestLogPanel: React.FC<QuestLogPanelProps> = ({
  socket,
  onClose,
  quests,
  onSetDestination,
}) => {
  const [questState, setQuestState] = useState<PlayerQuestState>({
    starterVoyageComplete: false,
    currentQuestIndex: 0,
    starterVoyageStartedAt: Date.now(),
    quests: {} as Record<StarterQuestId, QuestProgress>,
  });
  const [selectedQuestId, setSelectedQuestId] = useState<StarterQuestId | null>(null);
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(0, 0);

  React.useEffect(() => {
    if (!socket) return;

    const handleFullState = (data: PlayerQuestState): void => {
      setQuestState(data);
    };

    const handleQuestUpdate = (data: QuestUpdateEvent): void => {
      setQuestState((prev) => {
        const updated = { ...prev };
        updated.quests = { ...prev.quests };
        updated.quests[data.questId] = {
          questId: data.questId,
          status: data.status,
          currentCount: data.currentCount,
        };

        if (data.nextQuest) {
          updated.currentQuestIndex = quests.findIndex(q => q.id === data.nextQuest);
          updated.quests[data.nextQuest] = {
            questId: data.nextQuest,
            status: 'active',
            currentCount: 0,
          };
        }

        updated.starterVoyageComplete = data.starterVoyageComplete;

        return updated;
      });
    };

    socket.on('quest:fullState', handleFullState);
    socket.on('quest:update', handleQuestUpdate);

    // Request quest state when component mounts (in case we missed the initial send)
    socket.emit('quest:requestState');
    
    // Set up periodic quest state refresh to fix any locking issues
    const questRefreshInterval = setInterval(() => {
      socket.emit('quest:requestState');
    }, 10000); // Refresh every 10 seconds

    return () => {
      socket.off('quest:fullState', handleFullState);
      socket.off('quest:update', handleQuestUpdate);
      clearInterval(questRefreshInterval);
    };
  }, [socket, quests]);

  const getStatusColor = (status: 'locked' | 'active' | 'completed'): string => {
    switch (status) {
      case 'locked':
        return '#6b7280'; // Gray
      case 'active':
        return '#fbbf24'; // Gold/Yellow
      case 'completed':
        return '#10b981'; // Green
      default:
        return '#8b949e';
    }
  };

  const getStatusLabel = (status: 'locked' | 'active' | 'completed'): string => {
    switch (status) {
      case 'locked':
        return 'LOCKED';
      case 'active':
        return 'ACTIVE';
      case 'completed':
        return 'COMPLETE';
      default:
        return 'UNKNOWN';
    }
  };


  // Get quest progress, defaulting to locked if not found
  const getQuestProgress = (questId: StarterQuestId): QuestProgress => {
    return questState.quests[questId] || {
      questId,
      status: 'locked',
      currentCount: 0,
    };
  };

  // Calculate completion statistics
  const completedCount = quests.filter(q => {
    const progress = getQuestProgress(q.id);
    return progress.status === 'completed';
  }).length;

  const totalQuests = quests.length;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '10px',
        left: '10px',
        width: '520px',
        maxHeight: '85vh',
        background: 'rgba(13, 17, 23, 0.98)',
        border: '2px solid #30363d',
        fontFamily: 'var(--font-mono)',
        zIndex: 99,
        display: 'flex',
        flexDirection: 'column',
        ...dragStyle,
      }}
    >
      {/* Header */}
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: '#161b22',
          borderBottom: '2px solid #30363d',
          cursor: 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <div>
          <span style={{ color: '#58a6ff', fontSize: '12px', fontWeight: 'bold' }}>
            QUEST LOG
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#8b949e',
            cursor: 'pointer',
            fontSize: '12px',
            padding: '2px 6px',
          }}
        >
          [X]
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Quest List */}
        {quests.length > 0 ? (
          <>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px',
              }}
            >
              {quests.map((quest) => {
                const progress = getQuestProgress(quest.id);
                const statusColor = getStatusColor(progress.status);
                const isCompleted = progress.status === 'completed';
                const isActive = progress.status === 'active';
                const isLocked = progress.status === 'locked';

                return (
                  <div
                    key={quest.id}
                    onClick={() => setSelectedQuestId(quest.id)}
                    style={{
                      padding: '10px 12px',
                      marginBottom: '8px',
                      background: selectedQuestId === quest.id 
                        ? isCompleted 
                          ? 'rgba(16, 185, 129, 0.2)' 
                          : isActive
                          ? 'rgba(251, 191, 36, 0.2)'
                          : 'rgba(107, 114, 128, 0.2)'
                        : isActive 
                        ? 'rgba(251, 191, 36, 0.1)' 
                        : 'transparent',
                      border: selectedQuestId === quest.id
                        ? isCompleted
                          ? '1px solid rgba(16, 185, 129, 0.5)'
                          : isActive
                          ? '1px solid rgba(251, 191, 36, 0.5)'
                          : '1px solid rgba(107, 114, 128, 0.5)'
                        : isActive 
                        ? '1px solid rgba(251, 191, 36, 0.3)' 
                        : '1px solid transparent',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedQuestId !== quest.id) {
                        e.currentTarget.style.background = isCompleted 
                          ? 'rgba(16, 185, 129, 0.15)' 
                          : isActive
                          ? 'rgba(251, 191, 36, 0.15)'
                          : 'rgba(107, 114, 128, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedQuestId !== quest.id) {
                        e.currentTarget.style.background = isActive 
                          ? 'rgba(251, 191, 36, 0.1)' 
                          : 'transparent';
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <div
                        style={{
                          color: isCompleted 
                            ? '#10b981' 
                            : isActive 
                            ? '#fbbf24' 
                            : '#6b7280',
                          fontSize: '12px',
                          fontWeight: isActive ? 'bold' : 'normal',
                          textDecoration: isCompleted ? 'line-through' : 'none',
                          opacity: isLocked ? 0.6 : 1,
                        }}
                      >
                        {quest.name}
                      </div>
                      <div
                        style={{
                          display: 'inline-block',
                          background: statusColor,
                          color: '#0d1117',
                          padding: '2px 6px',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          borderRadius: '2px',
                        }}
                      >
                        {getStatusLabel(progress.status)}
                      </div>
                    </div>
                    <div
                      style={{
                        color: '#8b949e',
                        fontSize: '10px',
                        marginTop: '4px',
                        opacity: isCompleted ? 0.7 : isLocked ? 0.6 : 1,
                      }}
                    >
                      {isLocked ? 'Click to view requirements' : quest.objective}
                    </div>
                    {isActive && progress.status !== 'locked' && (
                      <div
                        style={{
                          marginTop: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            height: '4px',
                            background: '#21262d',
                            borderRadius: '2px',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              background: progress.status === 'completed'
                                ? '#10b981'
                                : '#58a6ff',
                              width: `${Math.min(
                                100,
                                (progress.currentCount / quest.targetCount) * 100
                              )}%`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            color: '#8b949e',
                            fontSize: '9px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {progress.currentCount}/{quest.targetCount}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Quest Details Panel */}
            {selectedQuestId && (() => {
              const selectedQuest = quests.find(q => q.id === selectedQuestId);
              if (!selectedQuest) return null;
              const selectedProgress = getQuestProgress(selectedQuestId);
              const isSelectedCompleted = selectedProgress.status === 'completed';
              const isSelectedActive = selectedProgress.status === 'active';
              const isSelectedLocked = selectedProgress.status === 'locked';
              
              // Find prerequisite quest
              const prerequisiteQuest = selectedQuest.order > 0 
                ? quests.find(q => q.order === selectedQuest.order - 1)
                : null;
              const prerequisiteProgress = prerequisiteQuest 
                ? getQuestProgress(prerequisiteQuest.id)
                : null;

              return (
                <div
                  style={{
                    borderTop: '2px solid #30363d',
                    background: '#161b22',
                    padding: '16px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3
                      style={{
                        color: isSelectedCompleted 
                          ? '#10b981' 
                          : isSelectedActive 
                          ? '#fbbf24' 
                          : '#6b7280',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        margin: 0,
                        textDecoration: isSelectedCompleted ? 'line-through' : 'none',
                      }}
                    >
                      {selectedQuest.name}
                    </h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedQuestId(null);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#8b949e',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0',
                        width: '20px',
                        height: '20px',
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', lineHeight: '1.5' }}>
                      {selectedQuest.description}
                    </div>
                  </div>

                  {/* Requirements for locked quests */}
                  {isSelectedLocked && (
                    <div style={{ 
                      marginBottom: '12px', 
                      padding: '8px', 
                      background: 'rgba(107, 114, 128, 0.1)',
                      border: '1px solid rgba(107, 114, 128, 0.3)',
                    }}>
                      <div style={{ color: '#f0883e', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                        WHY LOCKED:
                      </div>
                      {prerequisiteQuest ? (
                        <div style={{ color: '#8b949e', fontSize: '10px' }}>
                          {prerequisiteProgress?.status === 'completed' 
                            ? `[X] ${prerequisiteQuest.name} (COMPLETED)`
                            : `[ ] ${prerequisiteQuest.name} (NOT COMPLETED) - Complete this quest first`
                          }
                        </div>
                      ) : (
                        <div style={{ color: '#8b949e', fontSize: '10px' }}>
                          This quest is not yet available
                        </div>
                      )}
                    </div>
                  )}

                  {/* Objective */}
                  {!isSelectedLocked && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: '#58a6ff', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                        OBJECTIVE:
                      </div>
                      <div style={{ color: '#8b949e', fontSize: '10px' }}>
                        {selectedQuest.objective}
                      </div>
                      {selectedQuest.hint && (
                        <div style={{ color: '#6b7280', fontSize: '9px', marginTop: '4px', fontStyle: 'italic' }}>
                          Hint: {selectedQuest.hint}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Synopsis for completed quests */}
                  {isSelectedCompleted && (
                    <div style={{ 
                      marginBottom: '12px', 
                      padding: '8px', 
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    }}>
                      <div style={{ color: '#10b981', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                        COMPLETED:
                      </div>
                      <div style={{ color: '#8b949e', lineHeight: '1.5', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                        {selectedQuest.mentorDialogue?.complete || 'Quest completed successfully!'}
                      </div>
                    </div>
                  )}

                  {/* Rewards */}
                  {selectedQuest.rewards && selectedQuest.rewards.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: '#f0883e', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                        REWARDS:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {selectedQuest.rewards.map((reward, idx) => (
                          <div key={idx} style={{ color: '#8b949e', fontSize: '10px' }}>
                            {reward.type === 'pieces' && `${reward.amount?.toLocaleString()}₱`}
                            {reward.type === 'item' && `${reward.amount}x ${reward.itemName || reward.itemId}`}
                            {reward.type === 'xp' && `${reward.amount} XP`}
                            {reward.type === 'title' && `Title: ${reward.title}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Progress for active quests */}
                  {isSelectedActive && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ color: '#58a6ff', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                        PROGRESS:
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: '6px', 
                        background: '#21262d', 
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '4px',
                      }}>
                        <div
                          style={{
                            height: '100%',
                            background: '#58a6ff',
                            width: `${Math.min(100, (selectedProgress.currentCount / selectedQuest.targetCount) * 100)}%`,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <div style={{ color: '#8b949e', fontSize: '10px' }}>
                        {selectedProgress.currentCount} / {selectedQuest.targetCount}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Footer with completion statistics */}
            <div
              style={{
                padding: '12px 16px',
                borderTop: '2px solid #30363d',
                background: '#161b22',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#f0883e',
                  fontSize: '11px',
                  fontWeight: 'bold',
                }}
              >
                <span>Completed: {completedCount}/{totalQuests}</span>
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8b949e',
              fontSize: '12px',
              textAlign: 'center',
              padding: '40px 20px',
              minHeight: '200px',
            }}
          >
            No quests available
          </div>
        )}
      </div>
    </div>
  );
};
